import { Map, PinMarker, PixiLayer, PixiPinMarker } from "@sencrop/ui-map";
import * as L from "leaflet";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import {
  getAllActiveDevices,
  getAllInactiveDevices,
} from "../../../api/devices";
import { MAX_DISPLAYED_DEVICES } from "../../../constants";
import { Device } from "../../../models/device";
import { DeviceModel, getDeviceModelIcon } from "../../../models/device-model";
import { SearchLocation } from "../../../models/search-location";
import DeviceInfoCard from "../components/DeviceInfoCard";
import MapLoadingOverlay from "../components/MapLoadingOverlay";
import LocationCard from "../components/LocationCard";
import ZoneCircle from "../components/ZoneCircle";
import MapTopControls from "./MapTopControls";
import StatsCard from "../components/StatsCard";
import { useProgressiveRender } from "../../../hooks/useProgressiveRender";

const LOCATION_ZOOM = 9;

const DEFAULT_CIRCLE_RADIUS = 40;

const PIXI_LAYER_OPTIONS = {
  padding: 0.1,
  resolution: L.Browser.retina ? 2 : 1,
};

const DEFAULT_VISIBLE_MODELS = [
  DeviceModel.RAINCROP,
  DeviceModel.WINDCROP,
  DeviceModel.LEAFCROP,
  DeviceModel.SOLARCROP,
  DeviceModel.DWD,
  DeviceModel.THERMOCROP,
  DeviceModel.SOILCROP,
  DeviceModel.DAVIS,
  DeviceModel.MODULAR_STATION_4G,
  DeviceModel.METEUS,
  DeviceModel.OTHER,
];

const ZOOM_TRANSFORM = (zoom: number) => {
  if (zoom < 8) return 8;
  if (zoom < 11) return zoom + 1;
  return zoom;
};

const AllDevicesMap = () => {
  const [selectedDevice, setSelectedDevice] = useState<Device>();

  const [showInactiveDevices, setShowInactiveDevices] =
    useState<boolean>(false);

  const { isLoading: isLoadingActiveDevices, data: activeDevices = [] } =
    useQuery(["activeDevices"], getAllActiveDevices, {
      refetchOnWindowFocus: false,
    });

  const { isLoading: isLoadingInactiveDevices, data: inactiveDevices = [] } =
    useQuery(["inactiveDevices"], getAllInactiveDevices, {
      refetchOnWindowFocus: false,
    });

  const devices = useMemo(
    () => [...(showInactiveDevices ? inactiveDevices : []), ...activeDevices],
    [activeDevices, inactiveDevices, showInactiveDevices]
  );

  const isLoading = useMemo(
    () => isLoadingActiveDevices || isLoadingInactiveDevices,
    [isLoadingActiveDevices, isLoadingInactiveDevices]
  );

  const isNotLoading = useMemo(() => !isLoading, [isLoading]);

  const handleMarkerClick = (id: number) => {
    const device = devices.find((d) => d.id === id);
    setSelectedDevice((current) => (current === device ? undefined : device));
  };

  const handleCardClose = () => setSelectedDevice(undefined);

  const [location, setLocation] = useState<SearchLocation>();

  const mapRef = useRef<L.Map>();

  const handleLocationChange = (l?: SearchLocation) => {
    setLocation(l);

    if (l) mapRef.current?.setView(l, LOCATION_ZOOM);
  };

  const handleSearchDeviceChange = (value?: string) => {
    const device = displayedDevices.find(
      (d) => String(d.id) === value || d.identification === value
    );

    if (device) {
      setLocation({ lat: device.position.lat, lng: device.position.lng });
      mapRef.current?.setView(device.position, LOCATION_ZOOM);
    }
  };

  const [circleRadius, setCircleRadius] = useState<number>(
    DEFAULT_CIRCLE_RADIUS
  );

  const [visibleModels, setVisibleModels] = useState<DeviceModel[]>(
    DEFAULT_VISIBLE_MODELS
  );

  const handleToggleModel = (model: DeviceModel) => {
    setVisibleModels((value) =>
      value.includes(model)
        ? value.filter((v) => v !== model)
        : [...value, model]
    );
  };

  const filteredDevices = useMemo(
    () =>
      (MAX_DISPLAYED_DEVICES
        ? devices.slice(0, MAX_DISPLAYED_DEVICES)
        : devices
      ).filter((device) => visibleModels.includes(device.model)),
    [devices, visibleModels]
  );

  const {
    visibleItems: displayedDevices,
    isRendering,
    renderProgress,
  } = useProgressiveRender<Device>(filteredDevices);

  const handleToggleInactiveDevices = () =>
    setShowInactiveDevices((current) => !current);

  return (
    <Container>
      <Map
        topControls={
          isNotLoading && (
            <MapTopControls
              onSearchLocationChange={handleLocationChange}
              onSearchDeviceChange={handleSearchDeviceChange}
            />
          )
        }
        bottomControls={
          isNotLoading && (
            <>
              {location ? (
                <LocationCard
                  location={location}
                  locationRadius={circleRadius}
                  onLocationRadiusChange={setCircleRadius}
                  devices={devices}
                />
              ) : (
                <StatsCard
                  devices={devices}
                  displayedDevicesCount={filteredDevices.length}
                  visibleModels={visibleModels}
                  onToggleModelClick={handleToggleModel}
                  showInactiveDevices={showInactiveDevices}
                  onToggleInactiveDevices={handleToggleInactiveDevices}
                  inactiveDevicesCount={inactiveDevices.length}
                />
              )}
            </>
          )
        }
        whenCreated={(map) => (mapRef.current = map)}
      >
        <PixiLayer options={PIXI_LAYER_OPTIONS} zoomTransform={ZOOM_TRANSFORM}>
          {isNotLoading &&
            displayedDevices.map((device) => (
              <PixiPinMarker
                position={device.position}
                id={device.id}
                key={device.id}
                selected={selectedDevice?.id === device.id}
                onClick={handleMarkerClick}
                icon={getDeviceModelIcon(device.model)}
                size={40}
                variant={device.active ? "crop" : "disable"}
              />
            ))}
        </PixiLayer>
        {location && (
          <>
            {circleRadius ? (
              <ZoneCircle position={location} radius={circleRadius} />
            ) : null}
            <PinMarker
              position={location}
              icon="user"
              variant="position"
              size={50}
            />
          </>
        )}
      </Map>
      {selectedDevice && (
        <DeviceInfoCard device={selectedDevice} onClose={handleCardClose} />
      )}
      {isLoading && <MapLoadingOverlay />}
      {isNotLoading && isRendering && (
        <MapLoadingOverlay
          position={["bottom", "left"]}
          size={30}
          blocking={false}
        >
          {renderProgress} %
        </MapLoadingOverlay>
      )}
    </Container>
  );
};

export default AllDevicesMap;

const Container = styled.div`
  position: relative;
  grid-area: map;
  display: flex;
  width: 100%;
  height: 100%;
`;
